<script setup lang="ts">
import type {
  TMultisiteConsumption,
  TSiteConsumption,
} from "@/data/api/kili/consumption/types/multisite-consumption.type";

import { computed } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import LineChartDownIcon from "@/assets/icons/line-chart-down.svg?component";
import LineChartUpIcon from "@/assets/icons/line-chart-up.svg?component";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import TopFlopCard, { type ITopFlopItem } from "@/components/data/dashboard/top-flop-card.vue";

interface IProps {
  consumption?: TMultisiteConsumption;
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

function filterByConsumption(siteDsoConsumption: TSiteConsumption) {
  return !!siteDsoConsumption.consumption.value;
}

function filterByEvolutionGreaterThanZero(siteDsoConsumption: TSiteConsumption) {
  return !!siteDsoConsumption.evolution?.value && siteDsoConsumption.evolution?.value > 0;
}

function filterByEvolutionLessThanZero(siteDsoConsumption: TSiteConsumption) {
  return !!siteDsoConsumption.evolution?.value && siteDsoConsumption.evolution?.value < 0;
}

function mapConsumption(siteDsoConsumption: TSiteConsumption): ITopFlopItem {
  return {
    value: siteDsoConsumption.consumption.value as number,
    unit: siteDsoConsumption.consumption.unit,
    siteName: siteDsoConsumption.name,
  };
}

function mapEvolution(siteDsoConsumption: TSiteConsumption): ITopFlopItem {
  return {
    value: siteDsoConsumption.evolution.value as number,
    unit: siteDsoConsumption.evolution.unit,
    siteName: siteDsoConsumption.name,
  };
}

function generatePodium(
  filter: (siteDsoConsumption: TSiteConsumption) => boolean,
  map: (siteDsoConsumption: TSiteConsumption) => ITopFlopItem,
  sort: "asc" | "desc" = "desc",
) {
  if (!props.consumption) return [];

  return props.consumption.bySite
    .filter(filter)
    .map(map)
    .sort((a, b) => (sort === "desc" ? b.value - a.value : a.value - b.value))
    .slice(0, 3);
}

const top3HighestConsumptionSites = computed<ITopFlopItem[]>(() => {
  return generatePodium(filterByConsumption, mapConsumption, "desc");
});

const top3HighestConsumptionIncreaseSites = computed(() => {
  return generatePodium(filterByEvolutionGreaterThanZero, mapEvolution, "desc");
});

const top3HighestConsumptionDropSites = computed(() => {
  return generatePodium(filterByEvolutionLessThanZero, mapEvolution, "asc");
});
</script>

<template>
  <div class="max-xl:flex flex-col xl:grid grid-flow-col grid-cols-3 gap-6">
    <TopFlopCard
      :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.mostEnergyConsummingPodium"
      :is-loading="props.isLoading"
      :items="top3HighestConsumptionSites"
      :title="t(k.data.dashboard.topFlops.highestConsumption[EDashboardType.MULTISITE])"
    >
      <template #title-icon>
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <i class="pi pi-bolt !text-xs text-text-secondary" />
        </div>
      </template>
    </TopFlopCard>

    <TopFlopCard
      :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.highestConsumptionIncreasePodium"
      :is-loading="props.isLoading"
      :items="top3HighestConsumptionIncreaseSites"
      :title="t(k.data.dashboard.topFlops.highestPositiveEvolution[EDashboardType.MULTISITE])"
    >
      <template #title-icon>
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <LineChartUpIcon />
        </div>
      </template>
    </TopFlopCard>

    <TopFlopCard
      :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.highestConsumptionDecreasePodium"
      :is-loading="props.isLoading"
      :items="top3HighestConsumptionDropSites"
      :title="t(k.data.dashboard.topFlops.highestNegativeEvolution[EDashboardType.MULTISITE])"
    >
      <template #title-icon>
        <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
          <LineChartDownIcon />
        </div>
      </template>
    </TopFlopCard>
  </div>
</template>
